import { useI18n } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';

import { secondsSince } from '../format';

/** Re-renders every `intervalMs` so a relative time stays true without a new request. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [intervalMs]);
  return now;
}

/**
 * « Mis à jour il y a X secondes » (from the server's own `generatedAt`, so a copy served from the 60-second cache says how old it
 * really is), the refresh button, and the one-line note about the cache. The text is not a live region: it changes every second and a
 * screen reader must not announce it.
 */
export function FreshnessBar({
  generatedAt,
  refreshing,
  onRefresh,
}: {
  readonly generatedAt: string | null;
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
}): ReactElement {
  const { t } = useI18n();
  const now = useNow();

  let text = '';
  if (generatedAt !== null) {
    const seconds = secondsSince(generatedAt, now);
    if (seconds < 2)
      text =
        seconds === 1 ? t('admin.dashboard.updatedOneSecond') : t('admin.dashboard.updatedNow');
    else if (seconds < 120) text = t('admin.dashboard.updatedSeconds', { seconds });
    else text = t('admin.dashboard.updatedMinutes', { minutes: Math.floor(seconds / 60) });
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="text-sm text-inkSoft" data-testid="dashboard-updated" aria-live="off">
        {text}
      </p>
      <Button variant="secondary" size="sm" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? t('admin.dashboard.refreshing') : t('admin.dashboard.refresh')}
      </Button>
      <p className="text-xs text-inkFaint">{t('admin.dashboard.cacheNote')}</p>
    </div>
  );
}
